import React from 'react';
import { useTranslation } from 'react-i18next';
import { setHours, setMinutes } from 'date-fns';
import AppointmentCard from './AppointmentCard';
import type { Appointment, ClinicRoom } from '../../../services/clinicsService';

interface RoomSubColumnProps {
  room: ClinicRoom;
  date: Date;
  appointments: Appointment[];
  timeSlots: string[];
  isSlotAvailable: (timeStr: string) => boolean;
  onSlotClick?: (date: Date, room: ClinicRoom) => void;
  onAppointmentClick?: (app: Appointment, e: React.MouseEvent) => void;
  onRoomClick?: (room: ClinicRoom) => void;
}

export default function RoomSubColumn({
  room,
  date,
  appointments,
  timeSlots,
  isSlotAvailable,
  onSlotClick,
  onAppointmentClick,
  onRoomClick
}: RoomSubColumnProps) {
  const { i18n } = useTranslation();

  const roomColor = room.color_code || '#3b82f6';
  // Semi-transparent blueprint grid: larger 48px main square with 3x3 (9 sub-squares of 16px) inside
  const alphaMajor = roomColor.startsWith('#') && roomColor.length === 7 ? `${roomColor}33` : `color-mix(in srgb, ${roomColor} 20%, transparent)`;
  const alphaMinor = roomColor.startsWith('#') && roomColor.length === 7 ? `${roomColor}12` : `color-mix(in srgb, ${roomColor} 7%, transparent)`;

  const blueprintStyle: React.CSSProperties = {
    backgroundImage: `
      linear-gradient(to right, ${alphaMajor} 1px, transparent 1px),
      linear-gradient(to bottom, ${alphaMajor} 1px, transparent 1px),
      linear-gradient(to right, ${alphaMinor} 1px, transparent 1px),
      linear-gradient(to bottom, ${alphaMinor} 1px, transparent 1px)
    `,
    backgroundSize: '48px 48px, 48px 48px, 16px 16px, 16px 16px'
  };

  return (
    <div data-ui="room-sub-column-region-001"
      className="flex-1 min-w-[150px] border-r border-white/10 relative flex flex-col transition-all"
      style={{ boxShadow: `inset 0 0 25px ${room.color_code}26` }}
    >
      {/* Room Header */}
      <div data-ui="room-sub-column-region-002"
        className={`h-10 border-b border-white/10 flex items-center justify-center gap-2 sticky top-12 bg-black/90 backdrop-blur-md z-30 text-xs font-semibold text-white/90 ${onRoomClick ? 'cursor-pointer hover:bg-white/5 transition-colors' : ''}`}
        onClick={() => onRoomClick && onRoomClick(room)}
        title={onRoomClick ? 'Ver historial de la sala' : ''}
      >
        <div data-ui="room-sub-column-region-003" className="w-2.5 h-2.5 rounded-full shrink-0 shadow-sm" style={{ backgroundColor: room.color_code || '#808080' }} />
        <span className={`truncate pr-2 ${!room.is_active ? 'opacity-50 line-through' : ''}`}>
          {room.name} {!room.is_active && <span className="text-[10px] ml-1">{i18n.language === 'en' ? '(inactive)' : '(inactiva)'}</span>}
        </span>
      </div>

      {/* Time Slots Grid with Room Color Blueprint Grid Effect */}
      <div data-ui="room-sub-column-region-004" className="relative flex-1" style={blueprintStyle}>
        {timeSlots.map((time) => {
          const available = isSlotAvailable(time);
          return (
            <div
              data-ui="room-sub-column-region-005"
              data-slot-state={available ? 'available' : 'unavailable'}
              data-slot-boundary={time.endsWith(':00') ? 'hour' : 'half-hour'}
              key={time}
              onClick={() => {
                if (onSlotClick) {
                  const [h, m] = time.split(':').map(Number);
                  onSlotClick(setMinutes(setHours(date, h), m), room);
                }
              }}
              className={`h-12 border-b border-white/5 transition-colors cursor-pointer relative z-[1]
                ${available ? 'bg-white/[0.02] hover:bg-white/10' : 'bg-black/30 hover:bg-white/5'}
              `}
            />
          );
        })}

        {/* Appointments Layer */}
        {appointments.map(app => (
          <AppointmentCard
            key={app.id}
            appointment={app}
            onClick={onAppointmentClick}
          />
        ))}
      </div>
    </div>
  );
}
